import React, { useRef, useState } from 'react';

/* Inline icons keep the page free of an icon-library dependency. */
function AlertCircleIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true" data-id="element-8" data-mp="3a2yl92t92odj">
      <circle cx="12" cy="12" r="10" data-id="element-9" data-mp="308y585wfjyq1" />
      <path d="M12 8v4M12 16h.01" data-id="element-10" data-mp="2mb9vnu7tce0a" />
    </svg>;
}
function CheckCircleIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-11" data-mp="2rc7ca2d6czzo">
      <circle cx="12" cy="12" r="10" data-id="element-12" data-mp="2hej5ge2ao215" />
      <path d="m8 12.5 2.5 2.5L16 9.5" data-id="element-13" data-mp="alh176mgv4wu" />
    </svg>;
}
function SpinnerIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" aria-hidden="true" data-id="element-14" data-mp="fjsbfigobhfl">
      <path d="M12 3a9 9 0 1 0 9 9" data-id="element-15" data-mp="2qgqpx5ocvi31" />
    </svg>;
}

/** Live registration webhook (LeadConnector). */
const REGISTRATION_ENDPOINT = 'https://services.leadconnectorhq.com/hooks/RVFwacFn5Fa7BE70mBkx/webhook-trigger/4349a9f4-41d3-4740-8e30-65367553db8b';
type FieldName = 'nombre' | 'apellido' | 'telefono' | 'email' | 'upline';
type FormValues = Record<FieldName, string>;
const initialValues: FormValues = {
  nombre: '',
  apellido: '',
  telefono: '',
  email: '',
  upline: ''
};
const labels: Record<FieldName, string> = {
  nombre: 'Nombre',
  apellido: 'Apellido',
  telefono: 'Número de teléfono',
  email: 'Email',
  upline: '¿Quién te invitó o te refirió a este evento?'
};
const requiredMessages: Record<FieldName, string> = {
  nombre: 'Ingresa tu nombre.',
  apellido: 'Ingresa tu apellido.',
  telefono: 'Ingresa tu número de teléfono.',
  email: 'Ingresa tu email.',
  upline: 'Indica quién te invitó o te refirió a este evento.'
};
function validate(values: FormValues): Partial<Record<FieldName, string>> {
  const errors: Partial<Record<FieldName, string>> = {};
  (Object.keys(values) as FieldName[]).forEach(field => {
    if (!values[field].trim()) {
      errors[field] = requiredMessages[field];
    }
  });
  if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
    errors.email = 'Ingresa un correo electrónico válido.';
  }
  const digits = values.telefono.replace(/\D/g, '');
  if (values.telefono.trim() && digits.length < 10) {
    errors.telefono = 'Ingresa un número de teléfono válido de al menos 10 dígitos.';
  }
  return errors;
}
function buildPayload(values: FormValues) {
  return {
    first_name: values.nombre.trim(),
    last_name: values.apellido.trim(),
    full_name: `${values.nombre.trim()} ${values.apellido.trim()}`.trim(),
    phone: values.telefono.trim(),
    email: values.email.trim(),
    referred_by: values.upline.trim(),
    event: 'Agent Scaling Workshop',
    source: 'Landing Agent Scaling Workshop',
    submitted_at: new Date().toISOString()
  };
}
export function RegistrationForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const handleChange = (field: FieldName) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => prev[field] ? {
      ...prev,
      [field]: undefined
    } : prev);
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    setSubmitError(null);
    if (Object.keys(nextErrors).length > 0) {
      const firstField = (Object.keys(nextErrors) as FieldName[])[0];
      document.getElementById(firstField)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(REGISTRATION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(buildPayload(values))
      });
      if (!response.ok) throw new Error('request-failed');
      setValues(initialValues);
      setSubmitted(true);
      window.requestAnimationFrame(() => successRef.current?.focus());
    } catch {
      setSubmitError('No pudimos enviar tu registro en este momento. Tus datos siguen aquí; vuelve a intentarlo en unos minutos.');
      window.requestAnimationFrame(() => alertRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };
  return <section aria-labelledby="registro-heading" className="mx-auto w-full max-w-3xl px-6" data-id="element-16" data-mp="190swkv5mu5ah">
      <div className="rounded-2xl border border-violet-glow/30 bg-ink-800/80 p-6 shadow-[0_0_80px_-40px_rgba(168,85,247,0.9)] sm:p-10" data-id="element-17" data-mp="142rhsmf8os4t">
        {submitted ? <div ref={successRef} tabIndex={-1} role="status" className="flex flex-col items-center gap-4 py-6 text-center outline-none" data-id="element-18" data-mp="1it8j85rvji1p">
            <CheckCircleIcon className="h-10 w-10 text-violet-400" data-id="element-19" data-mp="1rtww2x5j7cd0" />
            <h2 id="registro-heading" className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl" data-id="element-20" data-mp="1mrxr5iu587dy">
              ¡Registro confirmado!
            </h2>
            <p className="max-w-md text-sm text-slate-300 sm:text-base" data-id="element-21" data-mp="1wrokhh6ywgcg">
              Recibimos tus datos para el Agent Scaling Workshop. Te contactaremos con los detalles
              de acceso.
            </p>
            <button type="button" onClick={() => setSubmitted(false)} className="mt-2 rounded-lg border border-violet-glow/40 px-5 py-2.5 text-sm font-semibold text-violet-200 transition-colors duration-150 ease-out hover:bg-violet-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400" data-id="element-22" data-mp="2f8jovwxxuez">
              Registrar a otra persona
            </button>
          </div> : <>
            <div className="text-center" data-id="element-23" data-mp="1f1h8walbkf62">
              <h2 id="registro-heading" className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl" data-id="element-24" data-mp="1f0fm58uxy48i">
                Reserva tu lugar
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-sm text-slate-300 sm:text-base" data-id="element-25" data-mp="1otel9ko2dd6d">
                Completa tus datos para registrarte en el Agent Scaling Workshop.
              </p>
            </div>

            {submitError && <div ref={alertRef} role="alert" tabIndex={-1} className="mt-8 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200 outline-none" data-id="element-26" data-mp="3j78uvk5ct9pf">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-none" data-id="element-27" data-mp="39b5uoju2bl54" />
                <span data-id="element-28" data-mp="34bt2djkf9lv5">{submitError}</span>
              </div>}

            <form onSubmit={handleSubmit} noValidate className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-29" data-mp="5rkqzbqghqs2">
              <Field name="nombre" value={values.nombre} error={errors.nombre} autoComplete="given-name" onChange={handleChange('nombre')} data-id="element-30" data-mp="3s7xcspxvbu7l" />
              <Field name="apellido" value={values.apellido} error={errors.apellido} autoComplete="family-name" onChange={handleChange('apellido')} data-id="element-31" data-mp="ok5d8abngxmw" />
              <Field name="telefono" type="tel" inputMode="tel" placeholder="(000) 000-0000" value={values.telefono} error={errors.telefono} autoComplete="tel" onChange={handleChange('telefono')} data-id="element-32" data-mp="3o8dk09gslpp1" />
              <Field name="email" type="email" inputMode="email" placeholder="ejemplo@ejemplo.com" value={values.email} error={errors.email} autoComplete="email" onChange={handleChange('email')} data-id="element-33" data-mp="bsjnw6nmlwzc" />
              <Field name="upline" placeholder="Nombre y apellido de la persona" value={values.upline} error={errors.upline} fullWidth onChange={handleChange('upline')} data-id="element-34" data-mp="6rvooxp8u9u1" />

              <div className="sm:col-span-2" data-id="element-35" data-mp="2jz3luh7x3aio">
                <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-6 py-3.5 font-display text-base font-bold uppercase tracking-[0.12em] text-white transition-colors duration-150 ease-out hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-400 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-36" data-mp="2f1bvvsqu1yh4">
                  {submitting && <SpinnerIcon className="h-4 w-4 animate-spin" data-id="element-37" data-mp="2a2ili96uenl6" />}
                  {submitting ? 'Enviando…' : 'Reservar mi lugar'}
                </button>
                <p className="mt-3 text-center text-xs text-slate-500" data-id="element-38" data-mp="259pch94cct3a">
                  Los campos marcados con <span className="text-violet-400" data-id="element-39" data-mp="1hjlcdbqd3bh1">*</span> son obligatorios.
                </p>
              </div>
            </form>
          </>}
      </div>
    </section>;
}
interface FieldProps {
  name: FieldName;
  value: string;
  error?: string;
  type?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric';
  placeholder?: string;
  autoComplete?: string;
  fullWidth?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}
function Field({
  name,
  value,
  error,
  type = 'text',
  inputMode,
  placeholder,
  autoComplete,
  fullWidth = false,
  onChange
}: FieldProps) {
  const errorId = `${name}-error`;
  return <div className={fullWidth ? 'sm:col-span-2' : undefined} data-id="element-40" data-mp="3qqj9chhev33s">
      <label htmlFor={name} className="block text-sm font-medium text-slate-200" data-id="element-41" data-mp="3lroozb3sp6hk">
        {labels[name]} <span className="text-violet-400" data-id="element-42" data-mp="3gtn72ti3vd7o">*</span>
      </label>
      <input id={name} name={name} type={type} inputMode={inputMode} placeholder={placeholder} autoComplete={autoComplete} value={value} onChange={onChange} required aria-required="true" aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} className={`mt-2 w-full rounded-lg border bg-ink-900 px-4 py-3 text-base text-white placeholder:text-slate-600 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-violet-500/70 ${error ? 'border-red-500/70' : 'border-white/10 focus:border-violet-500'}`} data-id="element-43" data-mp="3gr24u3dwav9e" />
      {error && <p id={errorId} className="mt-2 text-sm text-red-300" data-id="element-44" data-mp="14i3nn7m0wfy9">
          {error}
        </p>}
    </div>;
}